import React, { useEffect, useState } from 'react';
import { InstallNodeData } from '../../types';
import { ProposalRateSettings } from '../../utils/appSettings';
import { computeInstallNodeRawTotal } from '../../utils/proposalCalculations';
import { listInstallEquipmentAuditLines } from '../../utils/proposalInstallEquipment';
import InstallBreakdownGroups from './InstallBreakdownGroups';

interface InstallInputsProps {
  data: InstallNodeData;
  rates: ProposalRateSettings;
  onUpdate: (data: InstallNodeData) => void;
}

const InstallInputs: React.FC<InstallInputsProps> = ({ data, rates, onUpdate }) => {
  const [localData, setLocalData] = useState(data);

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const commit = (next: InstallNodeData) => {
    const overheadTotal = next.days * rates.overheadDailyRate;
    const updated = {
      ...next,
      overheadTotal,
      total: computeInstallNodeRawTotal({ ...next, overheadTotal }, rates),
    };
    setLocalData(updated);
    onUpdate(updated);
  };

  const handleChange = (field: 'days' | 'teamLaborPerDay', value: number) => {
    commit({ ...localData, [field]: value });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const laborTotal = localData.days * localData.teamLaborPerDay;
  const overheadTotal = localData.days * rates.overheadDailyRate;
  const equipmentRows = listInstallEquipmentAuditLines(localData, rates);

  return (
    <div className="node-inputs">
      <div className="input-section">
        <h4>Installation Labor</h4>

        <InstallBreakdownGroups
          install={localData}
          rates={rates}
          formatCurrency={formatCurrency}
          onInstallChange={commit}
          onDaysChange={(days) => handleChange('days', days)}
        />
      </div>

      <div className="calculation-breakdown">
        <div className="breakdown-row">
          <span>Team Labor ({localData.days} days)</span>
          <span>{formatCurrency(laborTotal)}</span>
        </div>
        {equipmentRows.map(row => (
          <div key={row.label} className="breakdown-row">
            <span>{row.label}</span>
            <span>{formatCurrency(row.amount)}</span>
          </div>
        ))}
        {(localData.fuelBudget || 0) > 0 && (
          <div className="breakdown-row">
            <span>Fuel</span>
            <span>{formatCurrency(localData.fuelBudget || 0)}</span>
          </div>
        )}
        <div className="breakdown-row">
          <span>Install Overhead ({localData.days} days)</span>
          <span>{formatCurrency(overheadTotal)}</span>
        </div>
        <div className="breakdown-row total">
          <span>Install Total</span>
          <span>{formatCurrency(localData.total)}</span>
        </div>
      </div>
    </div>
  );
};

export default InstallInputs;
